// Menu (job page round): a list of actions on the pop layer (PopLayer), under its button (MenuButton: the
// job's actions) or at the pointer (useContextMenu: a right-click on a tech's name on the board).
// - Its items come in sections, a hairline between them, each with an optional small label ("Go to").
//   An item may be a link, carry a hint on its right ("Today", a count), or be off with the reason it's
//   off under it ("Reopen the job first"). An item that is off can still be reached, so its reason is
//   read out; choosing it does nothing.
// - Something that can't be undone ("Cancel the job…") sits last, in its own section, and asks first in a
//   dialog; it is ink like the rest, never red: red is for a problem (DESIGN.md §2).
// - The keyboard: focus goes to the first item; ↑ ↓ move (round the ends), Home and End, a letter jumps to
//   the next item starting with it, Enter or Space chooses, Escape or Tab closes. Focus goes back to
//   what opened it.
// - Under a button it has a head line, its name beside Close; at the pointer it has none (a click
//   outside or Escape closes it), or the caller's own head (a live card).
// - Beside (the board's tech menu): next to what opened it, on its end side, level with its top and moved
//   up only as far as it must to stay on screen, kept under the page's banner; taller than the room, it
//   scrolls inside (place-beside.ts holds the sums). A right-click and the keyboard open it the same.
// - The title can carry a muted line under it (`note`), what the menu's choices stand at now.
// - A section can be one choice (`checked` on its items): each is a menuitemradio, the chosen one marked ●.
// The list is drawn here rather than with React Aria's Menu, which skips items that are off and so can't
// say why. Reads only --menu-*; the layer reads its own.
import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonProps } from '../Button/Button.tsx';
import { PopLayer } from '../Popover/Popover.tsx';
import { tokenPx } from '../token-value.ts';
import { placeBeside, type Box } from './place-beside.ts';
import './Menu.css';

/** Where a menu opens: at the pointer (a right-click), or at an element (a button, a name). */
export type MenuAt = { x: number; y: number } | { element: HTMLElement };

export interface MenuItem {
  id: string;
  label: string;
  /** On its right, muted: a number, "Today". */
  hint?: string;
  /** Off: the reason, under it ("Reopen the job first"). A hint on an item that's off is its reason. */
  reason?: string;
  isDisabled?: boolean;
  href?: string;
  /**
   * One of a few, the chosen one marked (●): a menuitemradio. The items of one section that carry it
   * are one choice (a technician's home division).
   */
  checked?: boolean;
}

export interface MenuSection {
  /** A small muted label over the section ("Go to"). */
  label?: string;
  items: MenuItem[];
}

export interface MenuProps {
  /** Where it's open, or null when it's closed. */
  at: MenuAt | null;
  onClose: () => void;
  /** Its name for screen readers ("Job 24612’s actions"). */
  label: string;
  /** The head line beside Close ("Job actions"). */
  title?: string;
  /** A muted line under the title ("Company default: grouped · yours: grouped"); the title goes ink. */
  note?: string;
  /** Above the items, not one of them: a live card (the board's tech menu). Replaces the title. */
  head?: ReactNode;
  sections: MenuSection[];
  onAction?: (id: string) => void;
  /**
   * Where it sits against its button; at the pointer it opens below and to the right. Beside: next to
   * what opened it (a name), on its end side, moved up only as far as it must (place-beside.ts).
   */
  placement?: 'bottom start' | 'bottom end' | 'beside';
  /** The menu's id, for its button's aria-controls. */
  id?: string;
}

/** How long a run of typed letters counts as one search (ms). */
const TYPEAHEAD_MS = 600;
/** Kept clear at the screen's edges (px): React Aria's own container padding, passed to it so its sums and
 * place-beside's agree. */
const EDGE_PX = 12;

/** Where the page's banner (its site header) ends on screen: 0 with none in view. */
function bannerBottom(): number {
  let bottom = 0;
  for (const el of document.querySelectorAll<HTMLElement>('header, [role="banner"]')) {
    // a header inside a section, an article or the like is that part's own head, not the page's banner
    if (
      el.getAttribute('role') !== 'banner' &&
      el.parentElement?.closest('article, aside, main, nav, section')
    )
      continue;
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.bottom > bottom) bottom = r.bottom;
  }
  return Math.min(bottom, window.innerHeight);
}

function reasonOf(it: MenuItem) {
  return it.isDisabled ? (it.reason ?? it.hint) : undefined;
}

export function Menu({
  at,
  onClose,
  label,
  title,
  head,
  note,
  sections,
  onAction,
  placement = 'bottom start',
  id,
}: MenuProps) {
  const point = useRef<HTMLSpanElement>(null);
  const element = useRef<HTMLElement | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const back = useRef<HTMLElement | null>(null);
  const typed = useRef({ text: '', at: 0 });
  const [, ready] = useState(0);
  const [active, setActive] = useState(0);
  const uid = useId();
  const atPointer = at !== null && 'x' in at;
  const beside = placement === 'beside';
  // beside, the layer hangs off a box Menu draws and sizes from the sums (the point), wherever it opened
  const onPoint = atPointer || beside;
  const [room, setRoom] = useState<number | undefined>(undefined);

  const visible = sections.filter((s) => s.items.length);
  const items = visible.flatMap((s) => s.items);

  // where it opens: the element, or a point drawn where the pointer was; and what focus goes back to
  useLayoutEffect(() => {
    if (at) {
      back.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      element.current = 'element' in at ? at.element : null;
      if (point.current && beside) {
        // until its size is known (the effect below), from the screen's corner with the whole screen as room
        Object.assign(point.current.style, { left: '0px', top: '0px', inlineSize: '0px', blockSize: '0px' });
      } else if ('x' in at && point.current) {
        point.current.style.left = `${at.x}px`;
        point.current.style.top = `${at.y}px`;
      }
      setActive(0);
    }
    ready((n) => n + 1);
  }, [at, beside]);

  const trigger = (onPoint ? point : element) as RefObject<HTMLElement | null>;
  const open = at !== null && trigger.current !== null;

  // beside: once it's drawn (its size known), where it sits, again as its contents or the screen change.
  // The point is a box from the screen's corner to the menu's top right: the layer hangs under it at its
  // end, so a new place is a new size, which React Aria watches and follows.
  useLayoutEffect(() => {
    if (!open || !beside || !at) return;
    const fit = () => {
      const body = list.current?.parentElement;
      const pop = body?.closest<HTMLElement>('.fl-pop');
      const frame = body?.parentElement;
      if (!body || !pop || !frame || !point.current) return;
      let anchor: Box;
      if ('x' in at) anchor = { left: at.x, top: at.y, width: 0, height: 0 };
      else {
        const r = at.element.getBoundingClientRect();
        anchor = { left: r.left, top: r.top, width: r.width, height: r.height };
      }
      const width = pop.offsetWidth;
      const place = placeBeside({
        anchor,
        menu: { width, height: frame.offsetHeight + body.scrollHeight - body.clientHeight },
        viewport: { width: window.innerWidth, height: window.innerHeight },
        keepBelow: bannerBottom(),
        gap: tokenPx('--pop-offset'),
        edge: EDGE_PX,
      });
      point.current.style.inlineSize = `${place.left + width}px`;
      point.current.style.blockSize = `${place.top}px`;
      setRoom(place.maxHeight);
    };
    fit();
    const watch = new ResizeObserver(fit);
    for (const el of [list.current, list.current?.previousElementSibling]) if (el) watch.observe(el);
    window.addEventListener('resize', fit);
    return () => {
      watch.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [open, beside, at]);

  const close = useCallback(() => {
    onClose();
    const to = back.current ?? element.current;
    requestAnimationFrame(() => to?.isConnected && to.focus());
  }, [onClose]);

  // focus follows the active item
  useLayoutEffect(() => {
    if (!open) return;
    list.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]')[active]?.focus();
  }, [open, active]);

  const choose = (it: MenuItem) => {
    if (it.isDisabled) return;
    onAction?.(it.id);
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const n = items.length;
    const move: Record<string, () => number> = {
      ArrowDown: () => (active + 1) % n,
      ArrowUp: () => (active - 1 + n) % n,
      Home: () => 0,
      End: () => n - 1,
    };
    if (move[e.key]) {
      e.preventDefault();
      setActive(move[e.key]!());
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (e.key.length === 1 && /\S/.test(e.key) && !e.altKey && !e.ctrlKey && !e.metaKey) {
      const now = Date.now();
      const t = typed.current;
      t.text = now - t.at < TYPEAHEAD_MS ? t.text + e.key.toLowerCase() : e.key.toLowerCase();
      t.at = now;
      // a run of one letter steps through the items starting with it; more letters narrow to one
      const from = t.text.length === 1 ? active + 1 : active;
      for (let k = 0; k < n; k++) {
        const i = (from + k) % n;
        if (items[i]!.label.toLowerCase().startsWith(t.text)) {
          setActive(i);
          break;
        }
      }
    }
  };

  let index = -1;
  const body = (
    <>
      {head ? <div className="fl-menu__head">{head}</div> : null}
      {!head && title ? (
        <p className="fl-menu__title" aria-hidden="true" data-noted={note ? '' : undefined}>
          {note ? (
            <>
              <span className="fl-menu__title-main">{title}</span>
              <span className="fl-menu__note">{note}</span>
            </>
          ) : (
            title
          )}
        </p>
      ) : null}
      <div
        ref={list}
        id={id}
        className="fl-menu"
        role="menu"
        aria-label={label}
        data-headed={head || title ? '' : undefined}
        onKeyDown={onKeyDown}
      >
        {visible.map((s, si) => {
          const labelId = s.label ? `${uid}-s${si}` : undefined;
          return (
            <div key={s.label ?? si} className="fl-menu__section" role="group" aria-labelledby={labelId}>
              {si > 0 ? <div className="fl-menu__sep" role="separator" /> : null}
              {s.label ? (
                <span id={labelId} className="fl-menu__label">
                  {s.label}
                </span>
              ) : null}
              {s.items.map((it) => {
                index += 1;
                const i = index;
                const reason = reasonOf(it);
                const reasonId = reason ? `${uid}-r${i}` : undefined;
                const radio = it.checked !== undefined;
                const props = {
                  role: radio ? 'menuitemradio' : 'menuitem',
                  'aria-checked': radio ? it.checked : undefined,
                  className: 'fl-menu__item',
                  tabIndex: i === active ? 0 : -1,
                  'aria-disabled': it.isDisabled || undefined,
                  'aria-describedby': reasonId,
                  'data-active': i === active ? '' : undefined,
                  onPointerMove: () => i !== active && setActive(i),
                  onClick: (e: MouseEvent) => {
                    if (it.isDisabled) e.preventDefault();
                    choose(it);
                  },
                  onKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (it.href && !it.isDisabled) (e.currentTarget as HTMLElement).click();
                      else choose(it);
                    }
                  },
                };
                const face = (
                  <>
                    <span className="fl-menu__line">
                      {radio ? (
                        <span className="fl-menu__mark" aria-hidden="true">
                          {it.checked ? '●' : ''}
                        </span>
                      ) : null}
                      <span className="fl-menu__text">{it.label}</span>
                      {it.hint && !it.isDisabled ? <span className="fl-menu__hint">{it.hint}</span> : null}
                    </span>
                    {reason ? (
                      <span id={reasonId} className="fl-menu__reason">
                        {reason}
                      </span>
                    ) : null}
                  </>
                );
                return it.href && !it.isDisabled ? (
                  <a key={it.id} {...props} href={it.href}>
                    {face}
                  </a>
                ) : (
                  <button key={it.id} type="button" {...props}>
                    {face}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <>
      {at && onPoint
        ? createPortal(<span ref={point} className="fl-menu-point" aria-hidden="true" />, document.body)
        : null}
      {at ? (
        <PopLayer
          triggerRef={trigger}
          label={label}
          isOpen={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          placement={beside ? 'bottom end' : atPointer ? 'bottom start' : placement}
          width="menu"
          showClose={!atPointer || !!head}
          offset={onPoint ? 0 : undefined}
          {...(beside
            ? { shouldFlip: false, edge: EDGE_PX, ...(room !== undefined ? { maxHeight: room } : {}) }
            : {})}
        >
          {body}
        </PopLayer>
      ) : null}
    </>
  );
}

export interface MenuButtonProps extends Omit<MenuProps, 'at' | 'onClose' | 'id'> {
  /** The button's words ("Job actions"). */
  children: ReactNode;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
}

/** A button that opens a Menu under it: aria-expanded and aria-controls kept right, a chevron after it. */
export function MenuButton({ children, variant, size, title, ...menu }: MenuButtonProps) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<MenuAt | null>(null);
  const id = useId();
  return (
    <span ref={anchor} className="fl-menu-anchor">
      <Button
        variant={variant}
        size={size}
        iconEnd="chevron-down"
        aria-haspopup="menu"
        aria-expanded={at !== null}
        aria-controls={at ? id : undefined}
        onPress={() => setAt(at ? null : anchor.current ? { element: anchor.current } : null)}
      >
        {children}
      </Button>
      <Menu
        {...menu}
        title={title ?? (typeof children === 'string' ? children : undefined)}
        id={id}
        at={at}
        onClose={() => setAt(null)}
      />
    </span>
  );
}

/**
 * A right-click menu for any element: spread `targetProps` on it. The pointer's spot opens it there;
 * the keyboard's menu key or Shift+F10 opens it at the element.
 */
export function useContextMenu() {
  const [at, setAt] = useState<MenuAt | null>(null);
  const targetProps = {
    onContextMenu: (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      setAt({ x: e.clientX, y: e.clientY });
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
        e.preventDefault();
        setAt({ element: e.currentTarget });
      }
    },
  };
  return { at, close: () => setAt(null), targetProps };
}
